import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { Link } from "react-router-dom";
import { ResponsiveTable } from "../../../components/common/ResponsiveTable";
import type {
  SalesCommand,
  SalesSource,
  SalesSources,
} from "../../../types/salesDesk";
import { base, load, money } from "./shared";

export function SalesSourcesPanel({
  command,
  busy,
  canEdit,
}: {
  command: SalesCommand;
  busy: boolean;
  canEdit: boolean;
}) {
  const today = new Date().toLocaleDateString("en-CA");
  const [from, setFrom] = useState(today.slice(0, 7) + "-01"),
    [to, setTo] = useState(today),
    [entry, setEntry] = useState(false),
    [selected, setSelected] = useState<SalesSource | null>(null),
    [reverse, setReverse] = useState<number | null>(null),
    [note, setNote] = useState(""),
    [error, setError] = useState("");
  const query = useQuery({
    queryKey: ["sales-desk", "sources", from, to],
    queryFn: () =>
      load<SalesSources>(base + `sources/?date_from=${from}&date_to=${to}`),
    enabled: !!from && !!to,
  });
  const data = query.data;
  const selectedRow =
    data?.rows.find((r) => r.key === selected?.key) || selected;
  const reverseEntry = async () => {
    try {
      await command("reverse_spend", { spend: reverse, note });
      setReverse(null);
      setNote("");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        alignItems={{ sm: "flex-end" }}
      >
        <FormControl>
          <FormLabel>Period starts</FormLabel>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Period ends</FormLabel>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </FormControl>
        {canEdit && (
          <Button onClick={() => setEntry(true)}>
            Record acquisition spend
          </Button>
        )}
      </Stack>
      {query.isPending && <CircularProgress />}
      {query.isError && <Alert color="danger">{query.error.message}</Alert>}
      {data && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",
                lg: "repeat(4,minmax(0,1fr))",
              },
              gap: 1.5,
            }}
          >
            {[
              { label: "Completed jobs", value: data.totals.completed_jobs },
              { label: "Sold value", value: money(data.totals.contract_value) },
              {
                label: "Recorded job costs",
                value: money(data.totals.actual_costs),
              },
              {
                label: "Recorded gross profit",
                value: money(data.totals.gross_profit),
              },
            ].map((c) => (
              <Card key={c.label}>
                <Typography level="body-sm">{c.label}</Typography>
                <Typography level="h3">{c.value}</Typography>
              </Card>
            ))}
          </Box>
          <Alert color="neutral">{data.basis}</Alert>
          <Card sx={{ p: { xs: 1, sm: 2 } }}>
            <ResponsiveTable
              rows={data.rows}
              getRowKey={(r) => r.key}
              columns={[
                {
                  key: "name",
                  header: "Source",
                  mobileVariant: "header",
                  render: (r) => (
                    <Box>
                      <Typography level="title-md">{r.name}</Typography>
                      <Typography level="body-xs">
                        {r.incomplete_jobs
                          ? `${r.incomplete_jobs} ${r.incomplete_jobs === 1 ? "job needs" : "jobs need"} cost review`
                          : r.wins
                            ? "Cost coverage reviewed on contributing jobs"
                            : "No completed jobs in this period"}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  key: "leads",
                  header: "Lead cohort",
                  render: (r) => (
                    <Typography level="body-sm">
                      {r.leads} leads · {r.contacted} reached · {r.booked}{" "}
                      booked
                    </Typography>
                  ),
                },
                {
                  key: "jobs",
                  header: "Completed work",
                  render: (r) => (
                    <Box>
                      <Typography level="body-sm">
                        {r.wins} {r.wins === 1 ? "job" : "jobs"}
                      </Typography>
                      <Typography level="body-xs">
                        {money(r.gross_profit)} gross profit
                      </Typography>
                    </Box>
                  ),
                },
                {
                  key: "spend",
                  header: "Acquisition spend",
                  render: (r) => (
                    <Box>
                      <Typography level="body-sm">
                        {r.spend_basis === "not recorded"
                          ? "Not recorded"
                          : money(r.acquisition_spend)}
                      </Typography>
                      <Typography level="body-xs">{r.spend_basis}</Typography>
                    </Box>
                  ),
                },
                {
                  key: "contribution",
                  header: "After acquisition",
                  render: (r) => (
                    <Box>
                      <Typography level="title-md">
                        {money(r.after_acquisition)}
                      </Typography>
                      <Typography level="body-xs">
                        Before overhead and commission; recorded costs may be
                        incomplete
                      </Typography>
                    </Box>
                  ),
                },
              ]}
              rowActions={(r) => (
                <Button size="sm" variant="soft" onClick={() => setSelected(r)}>
                  Inspect
                </Button>
              )}
            />
          </Card>
        </>
      )}
      <Modal open={entry} onClose={() => setEntry(false)}>
        <ModalDialog
          sx={{
            width: 560,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          {data && (
            <SpendForm
              choices={data.choices}
              command={command}
              busy={busy}
              done={() => setEntry(false)}
              today={today}
            />
          )}
        </ModalDialog>
      </Modal>
      <Modal
        open={!!selected}
        onClose={() => {
          setSelected(null);
          setReverse(null);
          setError("");
        }}
      >
        <ModalDialog
          sx={{
            width: 820,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          {selectedRow && (
            <Stack spacing={2}>
              <Typography level="h3" sx={{ pr: 4 }}>
                {selectedRow.name}
              </Typography>
              <Typography level="body-sm">
                One job is credited to one source. Open the Profit Desk to
                inspect costs, reconciliations, and completeness.
              </Typography>
              <Typography level="title-lg">Completed jobs</Typography>
              {selectedRow.jobs.length === 0 && (
                <Typography>No completed jobs in this period.</Typography>
              )}
              {selectedRow.jobs.map((job) => (
                <Card key={job.id}>
                  <Typography
                    component={Link}
                    to={`/operations/jobs/${job.id}`}
                  >
                    {job.name || `Job #${job.id}`}
                  </Typography>
                  <Typography level="body-sm">
                    Sold {money(job.contract_value)} − recorded costs{" "}
                    {money(job.actual_costs)} = gross profit{" "}
                    {money(job.gross_profit)}
                  </Typography>
                  {job.warnings.map((w, i) => (
                    <Typography key={i} color="warning" level="body-xs">
                      {w}
                    </Typography>
                  ))}
                </Card>
              ))}
              <Typography level="title-lg">Spending evidence</Typography>
              <Typography level="body-sm">
                Recorded actual: {money(selectedRow.actual_spend)} · Reviewed
                estimates: {money(selectedRow.estimated_spend)} · Prorated
                budget: {money(selectedRow.budget_estimate)}
              </Typography>
              {selectedRow.spend.length === 0 && (
                <Typography level="body-sm">
                  No charges or reviewed estimates have been recorded for this
                  period.
                </Typography>
              )}
              {selectedRow.spend.map((s, i) => (
                <Card key={s.id || s.reference + i} data-testid={`spend-entry-${s.id}`} variant="outlined">
                  <Typography level="title-sm">
                    {s.reference} · {money(s.amount)} · {s.basis}
                  </Typography>
                  <Typography level="body-sm">{s.note}</Typography>
                  <Typography level="body-xs">
                    {s.date} · {s.actor}
                    {s.reversed ? " · Reversed" : ""}
                  </Typography>
                  {canEdit && s.id && !s.reversed && !s.reverses && (
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => setReverse(s.id)}
                    >
                      Reverse entry
                    </Button>
                  )}
                </Card>
              ))}
              {reverse && (
                <Card color="warning">
                  <Typography level="title-md">
                    Reverse spending entry
                  </Typography>
                  <Typography level="body-sm">
                    The original stays in history. An offsetting entry corrects
                    its original reporting period.
                  </Typography>
                  <FormControl>
                    <FormLabel>Reversal reason</FormLabel>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </FormControl>
                  {error && <Alert color="danger">{error}</Alert>}
                  <Button
                    disabled={!note.trim()}
                    loading={busy}
                    color="warning"
                    onClick={() => void reverseEntry()}
                  >
                    Confirm reversal
                  </Button>
                </Card>
              )}
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
function SpendForm({
  choices,
  command,
  busy,
  done,
  today,
}: {
  choices: SalesSources["choices"];
  command: SalesCommand;
  busy: boolean;
  done: () => void;
  today: string;
}) {
  const [source, setSource] = useState("unattributed"),
    [date, setDate] = useState(today),
    [amount, setAmount] = useState(""),
    [basis, setBasis] = useState("actual"),
    [reference, setReference] = useState(""),
    [note, setNote] = useState(""),
    [error, setError] = useState("");
  const save = async () => {
    try {
      await command("spend", {
        source_key: source,
        occurred_on: date,
        amount,
        basis,
        reference,
        note,
      });
      done();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography level="h3" sx={{ pr: 4 }}>
        Record acquisition spend
      </Typography>
      <Typography level="body-sm">
        Record an existing charge or an estimate. This does not charge a card or
        create an accounting transaction. Direct-mail charges already appear
        automatically.
      </Typography>
      {error && <Alert color="danger">{error}</Alert>}
      <FormControl>
        <FormLabel>Lead source</FormLabel>
        <Select
          value={source}
          onChange={(_, v) => setSource(v || "unattributed")}
        >
          {choices.map((c) => (
            <Option value={c.key} key={c.key}>
              {c.name}
            </Option>
          ))}
        </Select>
      </FormControl>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Date incurred</FormLabel>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Amount (negative for credit)</FormLabel>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            slotProps={{ input: { step: ".01" } }}
          />
        </FormControl>
      </Stack>
      <FormControl>
        <FormLabel>Evidence basis</FormLabel>
        <Select value={basis} onChange={(_, v) => setBasis(v || "actual")}>
          <Option value="actual">Recorded actual charge / credit</Option>
          <Option value="estimate">Reviewed estimate</Option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Invoice or adjustment reference</FormLabel>
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Evidence and review note</FormLabel>
        <Textarea
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormControl>
      <Button
        loading={busy}
        disabled={!amount || !reference.trim() || !note.trim()}
        onClick={() => void save()}
      >
        Save spending entry
      </Button>
    </Stack>
  );
}
